import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../utils/axiosInstance';

const NotificationContext = createContext();

const STORAGE_KEY_ENABLED = 'notification_enabled';
const STORAGE_KEY_LAST_ORDER = 'last_seen_order_id';
const POLL_INTERVAL = 30000;
const MAX_POLL_INTERVAL = 300000; // 5분

export function NotificationProvider({ children }) {
  const [enabled, setEnabled] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY_ENABLED);
    return stored === null ? true : stored === 'true';
  });

  const [toasts, setToasts] = useState([]);

  const enabledRef = useRef(enabled);
  const failCountRef = useRef(0);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const toggleNotifications = useCallback(() => {
    setEnabled(prev => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY_ENABLED, String(next));
      return next;
    });
  }, []);

  const dismissToast = useCallback((toastId) => {
    setToasts(prev => prev.filter(t => t.id !== toastId));
  }, []);

  const pollForNewOrders = useCallback(async () => {
    if (!enabledRef.current) return;

    try {
      const res = await apiClient.get('/admin/orders', {
        params: { limit: 1, sort: 'order_date', order: 'desc' },
      });

      const data = res.data;
      const orders = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
      if (orders.length === 0) return;

      const latestOrderId = orders[0].order_id;
      const currentLastSeen = Number(localStorage.getItem(STORAGE_KEY_LAST_ORDER)) || null;

      if (currentLastSeen === null) {
        localStorage.setItem(STORAGE_KEY_LAST_ORDER, String(latestOrderId));
        return;
      }

      if (latestOrderId > currentLastSeen) {
        localStorage.setItem(STORAGE_KEY_LAST_ORDER, String(latestOrderId));

        setToasts(prev => [...prev, {
          id: Date.now(),
          orderId: latestOrderId,
          message: `새로운 주문이 접수되었습니다! (주문번호: #${latestOrderId})`,
        }]);
      }
      failCountRef.current = 0;
    } catch {
      failCountRef.current = Math.min(failCountRef.current + 1, 5);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    pollForNewOrders();
    const currentInterval = Math.min(
      POLL_INTERVAL * Math.pow(2, failCountRef.current),
      MAX_POLL_INTERVAL
    );
    const intervalId = setInterval(pollForNewOrders, currentInterval);
    return () => clearInterval(intervalId);
  }, [enabled, pollForNewOrders]);

  return (
    <NotificationContext.Provider value={{ enabled, toggleNotifications, toasts, dismissToast }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}
